import React, { useState, ReactElement, Children, isValidElement } from 'react'
import { StyledContainer, SlidingBackground, TabIconButton } from './styles'
import { Box, StackProps } from '@mui/material'

/*Only allow TabIconButton as children */
type AllowedChild = ReactElement<React.ComponentProps<typeof TabIconButton>>

export interface Props extends StackProps {
  children: AllowedChild[]
  defaultTabIndex?: number
  disabled?: boolean
}

/**
 * TabIconGroup is a group of TabIconButton components with sliding background highlight.
 * Only TabIconButton components are allowed as children.
 */
const TabIconGroup: React.FC<Props> = ({ children, defaultTabIndex = 0, disabled = false, ...stackProps }) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(defaultTabIndex)

  /* only allow TabIconButton */
  const childArray = Children.toArray(children).filter((child): child is AllowedChild => isValidElement(child) && child.type === TabIconButton)

  const { sx: stackSx, ...restStackProps } = stackProps

  return (
    <StyledContainer sx={{ width: 'fit-content', p: 0.5, ...stackSx }} {...restStackProps} disabled={disabled}>
      <Box position="relative" display="flex">
        <SlidingBackground selectedindex={selectedTabIndex} childcount={childArray.length} disabled={disabled} />

        {childArray.map((child, index) => {
          const isActive = selectedTabIndex === index

          const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
            setSelectedTabIndex(index)
            child.props?.onClick?.(event)
          }

          /* TabIconButton */
          const ChildComponent = child.type

          return <ChildComponent {...child.props} key={index} isActive={isActive} disabled={disabled} onClick={handleOnClick} />
        })}
      </Box>
    </StyledContainer>
  )
}

export default TabIconGroup
